import { MessageSquare, Plus, Trash2 } from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatDate } from '@/lib/date'
import type { AiConversation, AiMeta } from '@/types'
import Skeleton from '@/components/ui/Skeleton'

/** The admin's own chats, newest first, with this month's question count under them. */
export default function ConversationList({
  conversations,
  loading,
  activeId,
  onSelect,
  onCreate,
  onDelete,
  usage,
}: {
  conversations: AiConversation[]
  loading: boolean
  activeId: number | null
  onSelect: (id: number) => void
  onCreate: () => void
  onDelete: (conversation: AiConversation) => void
  usage?: AiMeta['usage']
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <button
        type="button"
        onClick={onCreate}
        className="mb-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-primary/60 bg-primary/20 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <Plus className="size-4" /> New chat
      </button>

      <div className="-mx-1 min-h-0 flex-1 space-y-0.5 overflow-y-auto px-1">
        {loading && (
          <div className="space-y-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        )}

        {!loading && conversations.length === 0 && (
          <p className="px-2 py-4 text-center text-xs text-foreground/50">Your chats appear here.</p>
        )}

        {conversations.map((conversation) => (
          <div
            key={conversation.id}
            className={cn(
              'group flex items-center gap-1 rounded-lg pr-1',
              conversation.id === activeId ? 'bg-foreground/5' : 'hover:bg-foreground/5',
            )}
          >
            <button
              type="button"
              onClick={() => onSelect(conversation.id)}
              className="flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left"
            >
              <MessageSquare className="size-3.5 shrink-0 text-foreground/40" />
              <span className="min-w-0 flex-1">
                <span className={cn('block truncate text-sm', conversation.id === activeId && 'font-semibold')}>
                  {conversation.title}
                </span>
                <span className="block text-2xs text-foreground/50">
                  {formatDate(conversation.last_message_at ?? conversation.created_at)}
                </span>
              </span>
            </button>
            <button
              type="button"
              aria-label="Delete chat"
              onClick={() => onDelete(conversation)}
              className="-m-1 shrink-0 rounded p-2 text-foreground/40 hover:text-danger sm:opacity-0 sm:group-hover:opacity-100"
            >
              <Trash2 className="size-3.5" />
            </button>
          </div>
        ))}
      </div>

      {usage && (
        <p className="mt-3 border-t border-foreground/10 pt-3 text-2xs text-foreground/50">
          {usage.cap === null
            ? `${usage.used} questions this month`
            : `${usage.used} of ${usage.cap} questions this month. Resets ${formatDate(usage.resets_on)}.`}
        </p>
      )}
    </div>
  )
}
